import { BaseIntegration } from '../../core/BaseIntegration';
import type { IntegrationConfig, IntegrationResult, IntegrationError, IntegrationParams } from '../../types';
import { registerIntegration } from '../../registry';

/** Represents a container in the in-memory store */
interface SimulatedContainer {
  id: string;
  name: string;
  image: string;
  status: 'created' | 'running' | 'paused' | 'exited' | 'dead';
  ports: Array<{ host: number; container: number; protocol: string }>;
  env: Record<string, string>;
  volumes: Array<{ host: string; container: string }>;
  command: string;
  labels: Record<string, string>;
  createdAt: number;
  startedAt: number | null;
  stoppedAt: number | null;
  logs: string[];
}

/** Represents a built image in the in-memory store */
interface SimulatedImage {
  id: string;
  tag: string;
  dockerfile: string;
  size: number;
  createdAt: number;
}

/**
 * Docker/Container integration with in-memory backend for development/testing.
 *
 * Provides container lifecycle operations (build, run, stop, remove, logs,
 * status, list) backed by an in-memory Map. No Docker SDK or daemon connection
 * is required.
 */
export class DockerIntegration extends BaseIntegration {
  private containers: Map<string, SimulatedContainer> = new Map();
  private images: Map<string, SimulatedImage> = new Map();

  constructor(config: IntegrationConfig) {
    super(config);

    const dockerHost = config.env.DOCKER_HOST;
    if (dockerHost) {
      this.logger.warn(
        'DOCKER_HOST is configured but real Docker client is not yet implemented. ' +
          'Falling back to in-memory simulation.',
        { dockerHost },
      );
    }

    this.logger.info('Docker integration initialized (in-memory simulation)');
  }

  async execute(
    action: string,
    params: IntegrationParams,
  ): Promise<IntegrationResult> {
    const validation = this.validateParams(action, params);
    if (!validation.valid) {
      return {
        success: false,
        error: {
          name: 'IntegrationError',
          message: 'Validation failed',
          code: 'VALIDATION_ERROR',
          details: validation.errors,
        } as IntegrationError,
        metadata: this.createMetadata(action, 0),
      };
    }

    const startTime = Date.now();

    try {
      let data: unknown;

      switch (action) {
        case 'build':
          data = await this.executeWithRetry(() => this.build(params));
          break;
        case 'run':
          data = await this.executeWithRetry(() => this.run(params));
          break;
        case 'stop':
          data = await this.executeWithRetry(() => this.stop(params));
          break;
        case 'remove':
          data = await this.executeWithRetry(() => this.removeContainer(params));
          break;
        case 'logs':
          data = await this.executeWithRetry(() => this.logs(params));
          break;
        case 'status':
          data = await this.executeWithRetry(() => this.status(params));
          break;
        case 'list':
          data = await this.executeWithRetry(() => this.list(params));
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      return {
        success: true,
        data,
        metadata: this.createMetadata(action, Date.now() - startTime),
      };
    } catch (error) {
      return this.handleError(action, error);
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** Generate a random hex container/image ID. */
  private generateId(): string {
    const segments: string[] = [];
    for (let i = 0; i < 16; i++) {
      segments.push(Math.floor(Math.random() * 256).toString(16).padStart(2, '0'));
    }
    return segments.join('');
  }

  /** Find a container by ID (prefix match supported). */
  private findContainer(containerId: string): SimulatedContainer | undefined {
    // Exact match first
    const exact = this.containers.get(containerId);
    if (exact) return exact;

    // Prefix match (like real Docker)
    for (const [id, container] of this.containers) {
      if (id.startsWith(containerId)) {
        return container;
      }
    }

    return undefined;
  }

  /** Generate realistic log lines for a container. */
  private generateLogLines(container: SimulatedContainer, count: number): string[] {
    const lines: string[] = [...container.logs];
    const baseTime = container.startedAt ?? container.createdAt;

    while (lines.length < count) {
      const ts = new Date(baseTime + lines.length * 1000).toISOString();
      lines.push(`${ts} [info] Container ${container.name} — heartbeat #${lines.length + 1}`);
    }

    return lines;
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  private async build(
    params: IntegrationParams,
  ): Promise<{ imageId: string; tag: string; size: number; buildTime: number }> {
    const dockerfile = (params.dockerfile as string | undefined) ?? 'Dockerfile';
    const tag = params.tag as string;
    const context = (params.context as string | undefined) ?? '.';
    const buildArgs = (params.buildArgs as Record<string, string> | undefined) ?? {};

    this.logger.debug('Docker BUILD', { dockerfile, tag, context, buildArgs });

    const imageId = `sha256:${this.generateId()}`;
    const size = 150_000_000 + Math.floor(Math.random() * 350_000_000); // 150–500 MB
    const buildTime = 2000 + Math.floor(Math.random() * 8000); // 2–10s simulated

    const image: SimulatedImage = {
      id: imageId,
      tag,
      dockerfile,
      size,
      createdAt: Date.now(),
    };

    this.images.set(tag, image);

    return { imageId, tag, size, buildTime };
  }

  private async run(
    params: IntegrationParams,
  ): Promise<{
    containerId: string;
    name: string;
    status: string;
    ports: Array<{ host: number; container: number; protocol: string }>;
  }> {
    const image = params.image as string;
    const name = (params.name as string | undefined) ?? `container-${this.generateId().slice(0, 12)}`;
    const rawPorts = (params.ports as Array<{ host: number; container: number; protocol?: string }> | undefined) ?? [];
    const env = (params.env as Record<string, string> | undefined) ?? {};
    const rawVolumes = (params.volumes as Array<{ host: string; container: string }> | undefined) ?? [];
    const command = (params.command as string | undefined) ?? '';

    this.logger.debug('Docker RUN', { image, name, ports: rawPorts.map(p => `${p.host}:${p.container}`).join(', '), volumes: rawVolumes.map(v => `${v.host}:${v.container}`).join(', '), command });

    const containerId = this.generateId();

    const ports = rawPorts.map((p) => ({
      host: p.host,
      container: p.container,
      protocol: p.protocol ?? 'tcp',
    }));

    const volumes = rawVolumes.map((v) => ({
      host: v.host,
      container: v.container,
    }));

    const container: SimulatedContainer = {
      id: containerId,
      name,
      image,
      status: 'running',
      ports,
      env,
      volumes,
      command,
      labels: {},
      createdAt: Date.now(),
      startedAt: Date.now(),
      stoppedAt: null,
      logs: [
        `${new Date().toISOString()} [info] Starting container ${name} from image ${image}`,
        `${new Date().toISOString()} [info] Container ${name} is now running`,
      ],
    };

    this.containers.set(containerId, container);

    return {
      containerId,
      name,
      status: container.status,
      ports,
    };
  }

  private async stop(
    params: IntegrationParams,
  ): Promise<{ containerId: string; status: string; stoppedAt: number }> {
    const containerId = params.containerId as string;

    this.logger.debug('Docker STOP', { containerId });

    const container = this.findContainer(containerId);
    if (!container) {
      throw new Error(`Container not found: ${containerId}`);
    }

    if (container.status !== 'running' && container.status !== 'paused') {
      throw new Error(`Container ${containerId} is not running (current status: ${container.status})`);
    }

    container.status = 'exited';
    container.stoppedAt = Date.now();
    container.logs.push(
      `${new Date().toISOString()} [info] Container ${container.name} stopped`,
    );

    return {
      containerId: container.id,
      status: container.status,
      stoppedAt: container.stoppedAt,
    };
  }

  private async removeContainer(
    params: IntegrationParams,
  ): Promise<{ containerId: string; removed: boolean }> {
    const containerId = params.containerId as string;
    const force = (params.force as boolean | undefined) ?? false;

    this.logger.debug('Docker REMOVE', { containerId, force });

    const container = this.findContainer(containerId);
    if (!container) {
      throw new Error(`Container not found: ${containerId}`);
    }

    if (container.status === 'running' && !force) {
      throw new Error(
        `Container ${containerId} is running. Stop it first or use force=true.`,
      );
    }

    this.containers.delete(container.id);

    return { containerId: container.id, removed: true };
  }

  private async logs(
    params: IntegrationParams,
  ): Promise<{ containerId: string; logs: string[]; lineCount: number }> {
    const containerId = params.containerId as string;
    const tail = (params.tail as number | undefined) ?? 100;
    const since = (params.since as string | undefined) ?? undefined;

    this.logger.debug('Docker LOGS', { containerId, tail, since });

    const container = this.findContainer(containerId);
    if (!container) {
      throw new Error(`Container not found: ${containerId}`);
    }

    let logLines = this.generateLogLines(container, tail);

    // Filter by since timestamp if provided
    if (since) {
      const sinceMs = new Date(since).getTime();
      logLines = logLines.filter((line) => {
        const timestampMatch = /^\d{4}-\d{2}-\d{2}T[\d:.]+Z/.exec(line);
        if (!timestampMatch) return true;
        return new Date(timestampMatch[0]).getTime() >= sinceMs;
      });
    }

    // Apply tail limit
    const tailedLines = logLines.slice(-tail);

    return {
      containerId: container.id,
      logs: tailedLines,
      lineCount: tailedLines.length,
    };
  }

  private async status(
    params: IntegrationParams,
  ): Promise<{
    containerId: string;
    name: string;
    image: string;
    status: string;
    ports: Array<{ host: number; container: number; protocol: string }>;
    createdAt: number;
    startedAt: number | null;
    stoppedAt: number | null;
  }> {
    const containerId = params.containerId as string;

    this.logger.debug('Docker STATUS', { containerId });

    const container = this.findContainer(containerId);
    if (!container) {
      throw new Error(`Container not found: ${containerId}`);
    }

    return {
      containerId: container.id,
      name: container.name,
      image: container.image,
      status: container.status,
      ports: container.ports,
      createdAt: container.createdAt,
      startedAt: container.startedAt,
      stoppedAt: container.stoppedAt,
    };
  }

  private async list(
    params: IntegrationParams,
  ): Promise<{
    containers: Array<{
      id: string;
      name: string;
      image: string;
      status: string;
      ports: Array<{ host: number; container: number; protocol: string }>;
      createdAt: number;
    }>;
    total: number;
  }> {
    const showAll = (params.all as boolean | undefined) ?? false;
    const filterByLabel = (params.filterByLabel as string | undefined) ?? undefined;

    this.logger.debug('Docker LIST', { all: showAll, filterByLabel });

    let entries = Array.from(this.containers.values());

    // By default, only show running containers (like `docker ps`)
    if (!showAll) {
      entries = entries.filter((c) => c.status === 'running');
    }

    // Filter by label if specified
    if (filterByLabel) {
      const [labelKey, labelValue] = filterByLabel.split('=');
      entries = entries.filter((c) => {
        if (labelValue !== undefined) {
          return c.labels[labelKey] === labelValue;
        }
        return labelKey in c.labels;
      });
    }

    // Sort by creation time (newest first)
    entries.sort((a, b) => b.createdAt - a.createdAt);

    const containers = entries.map((c) => ({
      id: c.id,
      name: c.name,
      image: c.image,
      status: c.status,
      ports: c.ports,
      createdAt: c.createdAt,
    }));

    return { containers, total: containers.length };
  }
}

// Auto-register
registerIntegration('docker', DockerIntegration);
